import { useMemo } from 'react';
import { Box, Typography, Grid, Badge, Tooltip } from '@mui/material';

// Use unified date utils
import { moment } from '../../utils/dateUtils';

const CancelledMonthView = ({ date, appointments, technicians, onDateClick }) => {
  // moment.tz.setDefault is now configured in dateUtils.js

  // Get calendar days for the month
  const calendarDays = useMemo(() => {
    const startOfMonth = moment(date).startOf('month');
    const endOfMonth = moment(date).endOf('month');
    const startOfCalendar = moment(startOfMonth).startOf('week');
    const endOfCalendar = moment(endOfMonth).endOf('week');
    
    const days = [];
    let currentDay = moment(startOfCalendar);
    
    while (currentDay.isSameOrBefore(endOfCalendar)) {
      days.push(moment(currentDay));
      currentDay.add(1, 'day');
    }
    
    return days;
  }, [date]);

  // Group appointments by day
  const appointmentsByDay = useMemo(() => {
    const grouped = {};
    
    appointments?.forEach(apt => {
      if (!apt.startTime) return;
      
      const dayKey = moment(apt.startTime).format('YYYY-MM-DD');
      if (!grouped[dayKey]) {
        grouped[dayKey] = [];
      }
      grouped[dayKey].push(apt);
    });
    
    return grouped;
  }, [appointments]);

  // Get week day headers
  const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  return (
    <Box>
      {/* Week Day Headers */}
      <Grid container spacing={0.5} sx={{ mb: 1 }}>
        {weekDays.map((day, idx) => (
          <Grid item xs={12/7} key={idx}>
            <Box sx={{ 
              p: 1, 
              textAlign: 'center', 
              fontWeight: 'bold',
              backgroundColor: '#f5f5f5',
              borderRadius: 1,
            }}>
              {day}
            </Box>
          </Grid>
        ))}
      </Grid>

      {/* Calendar Grid */}
      <Grid container spacing={0.5}>
        {calendarDays.map((day, idx) => {
          const dayKey = day.format('YYYY-MM-DD');
          const dayAppointments = appointmentsByDay[dayKey] || [];
          const isCurrentMonth = day.month() === moment(date).month();
          const isToday = day.isSame(moment(), 'day');

          return (
            <Grid item xs={12/7} key={idx}>
              <Tooltip
                title={dayAppointments.length > 0 ? (
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                      {day.format('dddd, MMM D')}
                    </Typography>
                    {dayAppointments.slice(0, 5).map((apt, i) => (
                      <Typography key={i} variant="caption" sx={{ display: 'block' }}>
                        • {apt.type?.name}: {apt.property?.zipCode || 'No ZIP'}
                      </Typography>
                    ))}
                    {dayAppointments.length > 5 && (
                      <Typography variant="caption" sx={{ fontStyle: 'italic' }}>
                        +{dayAppointments.length - 5} more
                      </Typography>
                    )}
                  </Box>
                ) : ''}
                placement="top"
                arrow
              >
                <Box
                  onClick={() => onDateClick(day.format())}
                  sx={{
                    minHeight: 80,
                    p: 0.5,
                    backgroundColor: isToday ? '#FFF3E0' : (isCurrentMonth ? '#fff' : '#f5f5f5'),
                    border: isToday ? '2px solid #E65100' : '1px solid #e0e0e0',
                    borderRadius: 1,
                    cursor: 'pointer',
                    opacity: isCurrentMonth ? 1 : 0.5,
                    '&:hover': {
                      backgroundColor: '#f0f0f0',
                    },
                  }}
                >
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      fontWeight: isToday ? 'bold' : 'normal',
                      color: isToday ? '#E65100' : 'inherit',
                    }}
                  >
                    {day.format('D')}
                  </Typography>
                  
                  {dayAppointments.length > 0 && (
                    <Badge 
                      badgeContent={dayAppointments.length} 
                      color="error"
                      sx={{ 
                        display: 'block',
                        mt: 1,
                        '& .MuiBadge-badge': {
                          position: 'relative',
                          transform: 'none',
                        }
                      }}
                    >
                      <Typography variant="caption" sx={{ color: '#d32f2f', fontWeight: 'bold' }}>
                        Cancelled
                      </Typography>
                    </Badge>
                  )}
                </Box>
              </Tooltip>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
};

export default CancelledMonthView;
